import { Injectable } from '@angular/core';
import { AuthService } from '@services';
import dexFreight from '../microservices';

export type TelemetryFilters = {
    from?: string,
    to?: string,
    includeInternal?: boolean,
    userId?: string,
    companyId?: string,
};

export type TelemetryOverview = {
    totals: { sessions: number, users: number, companies: number, anonymousSessions: number, events: number, activeMs: number },
    perDay: { day: string, sessions: number, users: number }[],
    routes: { route: string, visits: number, activeMs: number, avgActiveMs: number }[],
    clicks: { route: string, name: string, count: number }[],
    api: { name: string, total: number, failed: number, avgMs: number }[],
    apiFailures: { name: string, code: string, count: number }[],
    errors: { name: string, message: string, count: number, sessions: number, lastAt: string }[],
};

export type TelemetrySessionRow = {
    sessionId: string,
    startedAt: string,
    lastSeenAt: string,
    endReason?: string,
    entryRoute?: string,
    userId?: string,
    userName?: string,
    userEmail?: string,
    companyName?: string,
    role?: string,
    impersonated: boolean,
    events: number,
    failures: number,
    activeMs: number,
};

export type TelemetryEvent = {
    seq: number,
    ts: string,
    type: string,
    name: string,
    route?: string,
    routeParams?: Record<string, string>,
    props?: Record<string, unknown>,
    durationMs?: number,
    actor?: string,
    traceId?: string,
};

export type TelemetrySessionDetail = {
    session: TelemetrySessionRow & { userAgent?: string, viewport?: string, impersonatedBy?: string },
    events: TelemetryEvent[],
    truncated: boolean,
};

export type TelemetryPrivacy = {
    user: { _id: string, name: string, email: string, role: string },
    summary: {
        sessions: number,
        events: number,
        firstAt?: string,
        lastAt?: string,
        optedOut: boolean,
        requests: { kind: string, recordedVia: string, reference?: string, deletedSessions?: number, deletedEvents?: number, createdAt: string }[],
    },
};

/**
 * La telemetría de uso para el tablero de `#/telemetry`. Todos los endpoints exigen un superadmin
 * —con su sesión o entrando como otro usuario— y lo deciden en el backend; aquí solo se pasa el token.
 */
@Injectable({
    providedIn: 'root'
})
export class TelemetryAdminService {

    constructor (private auth: AuthService) {}

    /** Si quien mira puede abrir el tablero, y cómo: con su sesión de superadmin o entrando como otro usuario. */
    async access (): Promise<{ allowed: boolean, via?: 'superadmin' | 'impersonation' }> {
        const token = await this.auth.token.get();
        if (!token) return { allowed: false };
        try {
            return (await this.api().canViewTelemetry(token)) ?? { allowed: false };
        } catch {
            return { allowed: false };
        }
    }

    async overview (filters: TelemetryFilters): Promise<TelemetryOverview> {
        return await this.api().getTelemetryOverview(filters, await this.token());
    }

    async sessions (filters: TelemetryFilters, page: number): Promise<{ rows: TelemetrySessionRow[], total: number }> {
        return await this.api().listTelemetrySessions(filters, page, await this.token());
    }

    async session (sessionId: string): Promise<TelemetrySessionDetail> {
        return await this.api().getTelemetrySession(sessionId, await this.token());
    }

    async privacy (email: string): Promise<TelemetryPrivacy> {
        return await this.api().getTelemetryPrivacy(email, await this.token());
    }

    async deleteUserData (userId: string, reference: string): Promise<{ sessions: number, events: number }> {
        return await this.api().deleteTelemetryUserData(userId, reference, await this.token());
    }

    async setOptOut (userId: string, optOut: boolean, reference: string): Promise<{ optedOut: boolean }> {
        return await this.api().setTelemetryOptOut(userId, optOut, reference, await this.token());
    }

    /** El backend contesta `Unauthorized` a quien no es superadmin; la vista lo trata como acceso retirado. */
    isUnauthorized (error: unknown): boolean {
        const message = error instanceof Error ? error.message : String(error ?? '');
        return /superadmin/i.test(message) || (error as { code?: string })?.code === 'Unauthorized';
    }

    private async token (): Promise<string> {
        return (await this.auth.token.get()) ?? '';
    }

    /** Los tipos del backend se generan aparte (`interfaces.d.ts`); el módulo nuevo se tipa aquí. */
    private api (): any {
        return (dexFreight as any).Telemetry;
    }
}
